/**
 * Click-outside-to-close for `<theme-drawer>` in sidebar mode.
 *
 * On wide viewports (>= 990px) the drawer is a non-modal dialog opened with
 * `show()`. It squeezes page content via a `margin-right` on `.page-wrapper`
 * rather than covering it, so there is no `::backdrop` and page content stays
 * fully interactive. The drawer's own `#onBackdropClick` handler is bound to
 * the dialog element, so clicks on the page never reach it — by design, it is
 * inert in this mode.
 *
 * This module adds the missing behaviour: a click anywhere outside an open
 * sidebar drawer closes it.
 *
 * Modal mode (< 990px) is deliberately untouched — `#onBackdropClick` already
 * handles it correctly there, and adding a second closer would risk
 * double-closing or fighting the native dialog semantics.
 */

/** Viewport width at/above which drawers render as a squeeze sidebar. Mirrors MODAL_BREAKPOINT in theme-drawer.js. */
const SIDEBAR_BREAKPOINT = 990;

const sidebarQuery = window.matchMedia(`(min-width: ${SIDEBAR_BREAKPOINT}px)`);

/**
 * Closes the topmost open sidebar drawer when the click landed outside it.
 *
 * Bound on the capture phase so it still runs if page content stops
 * propagation, but it never calls preventDefault — the click continues to do
 * whatever it would normally do. Closing the drawer is additive.
 *
 * @param {MouseEvent} event
 */
function onDocumentClick(event) {
  if (!sidebarQuery.matches) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const openDrawers = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('theme-drawer[open]')));
  if (!openDrawers.length) return;

  // A click on the trigger must not close the drawer here: the trigger's own
  // toggle() runs afterwards and would immediately reopen it, producing a
  // close/reopen flicker. Triggers are linked to their drawer by aria-controls,
  // the same convention theme-drawer.js uses to restore focus on close.
  const trigger = target.closest('[aria-controls]');
  const controls = trigger?.getAttribute('aria-controls');

  // Close from the top of the stack down, so the visually frontmost drawer
  // goes first and one click closes one drawer.
  const sorted = openDrawers.sort(
    (a, b) =>
      Number(b.style.getPropertyValue('--drawer-stack-order') || 0) -
      Number(a.style.getPropertyValue('--drawer-stack-order') || 0)
  );

  for (const drawer of sorted) {
    if (drawer.contains(target)) return;
    if (controls && controls === drawer.id) return;

    // Don't close underneath an open nested dialog (e.g. a confirmation modal
    // launched from inside the drawer) — that dialog owns the interaction.
    // The drawer's panel is itself a dialog[open], so scope the lookup to
    // inside the panel, matching theme-drawer.js's own nested-dialog check.
    const panel = drawer.querySelector('dialog');
    if (panel?.querySelector('dialog[open]')) return;

    /** @type {{ close?: () => void }} */ (drawer).close?.();
    return;
  }
}

document.addEventListener('click', onDocumentClick, true);
